/**
 * The EMT's readiness view behind flag `emt_readiness_v2` (pure rules: src/lib/readiness/emtReadinessView.ts).
 * Used by the Readiness page (EMTPerformance) and the EMT dashboard. Status is always shown as text plus
 * an icon, never by colour alone; colours come from the --rr-success / --rr-warning / --rr-info tokens,
 * whose -fg variants keep text at AA contrast on their tints in light and dark mode.
 */
import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, CheckCircle2, Compass, GraduationCap, PartyPopper, Sparkles, Sprout, TrendingUp, Trophy, X } from 'lucide-react';
import { calculateReadiness } from '../../lib/readiness/calculateReadiness';
import { toReadinessProfile } from '../../lib/readiness/profileAdapter';
import {
  allPartsComplete,
  celebrationAction,
  competitivenessGrade,
  nextBestStep,
  partStatuses,
  readinessSuggestions,
  type CompetitivenessGrade,
  type DriverReadinessInput,
  type GradeView,
  type NextBestStep,
  type ReadinessPartView,
  type ReadinessSuggestion,
} from '../../lib/readiness/emtReadinessView';
import { getEmtCertificationNames } from '../../lib/certificationMatching';
import { resolveCanonicalCertification } from '../../lib/constants/certifications';
import { DRIVER_ROLES_FLAG, isDispatcherProfile, isNonEmsProfile, profileHasDriverItem } from '../../lib/jobs/driverRoles';
import { useFeatureFlag } from '../../context/FeatureFlagsContext';
import { findTrainingHref } from '../../lib/institutes';
import { upsertProfile } from '../../lib/emtRepo';
import { usePrefersReducedMotion } from '../../hooks/usePrefersReducedMotion';
import { useToaster } from '../ui/Toaster';
import Confetti from '../ui/Confetti';

type AnyProfile = Record<string, unknown> | null | undefined;

const PRIMARY_LINK =
  'inline-flex min-h-11 items-center justify-center rounded-xl px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-transform hover:-translate-y-px focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[var(--rr-red)]';
const SECONDARY_LINK =
  'inline-flex min-h-10 items-center justify-center rounded-lg border border-[var(--border-default)] bg-[var(--surface-card)] px-4 py-2 text-sm font-semibold text-[var(--text-primary)] hover:bg-[var(--surface-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--rr-info)]';

function prefsOf(profile: AnyProfile): { readinessDismissed?: unknown; readinessCelebrated?: unknown; publicProfile?: unknown } {
  const p = (profile as { preferences?: unknown } | null)?.preferences;
  return p && typeof p === 'object' ? (p as Record<string, unknown>) : {};
}

/** Everything the v2 view needs, from the same adapter + breakdown employers' numbers come from. */
export function useEmtReadinessView(profile: AnyProfile, dismissed: readonly string[]) {
  const readinessProfile = useMemo(() => toReadinessProfile(profile as Parameters<typeof toReadinessProfile>[0]), [profile]);
  const breakdown = useMemo(() => (readinessProfile ? calculateReadiness(readinessProfile) : null), [readinessProfile]);
  // driver_roles: a person who chose "Driver / non-certified" sees a view about driving credentials.
  const driverRoles = useFeatureFlag(DRIVER_ROLES_FLAG);
  return useMemo(() => {
    if (!breakdown) return null;
    const heldCertNames = getEmtCertificationNames(profile as Parameters<typeof getEmtCertificationNames>[0]);
    const driver: DriverReadinessInput | null = isNonEmsProfile(profile, driverRoles)
      ? {
          dispatcher: isDispatcherProfile(profile, driverRoles),
          hasDriversLicense: profileHasDriverItem(profile, 'drivers_license'),
          hasDrivingRecord: profileHasDriverItem(profile, 'dmv_record'),
          hasCprBls: profileHasDriverItem(profile, 'bls'),
          hasEvoc: heldCertNames.some((n) => resolveCanonicalCertification(n) === 'EVOC'),
        }
      : null;
    const parts = partStatuses(breakdown, { driver });
    const grade = competitivenessGrade(parts);
    const suggestions = readinessSuggestions(breakdown, {
      dismissed,
      publicProfile: prefsOf(profile).publicProfile === false ? false : undefined,
      heldCertNames,
      driver,
    });
    const step = nextBestStep(breakdown, parts, suggestions);
    return { breakdown, parts, grade, suggestions, step, complete: allPartsComplete(parts) };
  }, [breakdown, dismissed, profile, driverRoles]);
}

/** Dismissed suggestion ids: the profile's preferences.readinessDismissed plus this session's, saved through upsertProfile. */
export function useReadinessDismissals(uid: string | undefined, profile: AnyProfile) {
  const { push } = useToaster();
  const stored = prefsOf(profile).readinessDismissed;
  const fromProfile = useMemo(() => (Array.isArray(stored) ? stored.filter((x): x is string => typeof x === 'string') : []), [stored]);
  const [local, setLocal] = useState<string[]>([]);
  const dismissed = useMemo(() => [...new Set([...fromProfile, ...local])], [fromProfile, local]);
  const dismiss = useCallback(
    async (id: string) => {
      if (!uid) return;
      setLocal((prev) => [...prev, id]);
      const next = [...new Set([...fromProfile, ...local, id])].slice(-50);
      try {
        await upsertProfile(uid, { preferences: { readinessDismissed: next } } as never);
      } catch (err) {
        console.warn('[readiness] could not save dismissal', err);
        setLocal((prev) => prev.filter((x) => x !== id));
        push({ title: 'Could not hide that suggestion', description: 'Check your connection and try again.' });
      }
    },
    [uid, fromProfile, local, push],
  );
  return { dismissed, dismiss };
}

/* ------------------------------------------------------------------ Grade */

const GRADE_STYLE: Record<CompetitivenessGrade, { bg: string; fg: string; border: string; Icon: typeof Sprout }> = {
  // Same colours as the v3 Competitiveness card (ReadinessV3 TONE_STYLE): Building amber, Competitive blue.
  building: { bg: 'var(--rr-warning-light)', fg: 'var(--rr-warning-fg)', border: 'var(--rr-warning)', Icon: Sprout },
  competitive: { bg: 'var(--rr-info-light)', fg: 'var(--rr-info)', border: 'var(--rr-info)', Icon: TrendingUp },
  strong: { bg: 'var(--rr-success-light)', fg: 'var(--rr-success-fg, var(--rr-success))', border: 'var(--rr-success)', Icon: Trophy },
};
const GRADE_ORDER: CompetitivenessGrade[] = ['building', 'competitive', 'strong'];

export function ReadinessGradeChip({ grade }: { grade: GradeView }) {
  const s = GRADE_STYLE[grade.grade];
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm font-semibold"
      style={{ background: s.bg, color: s.fg, borderColor: s.border }}
      data-testid="readiness-grade"
      data-grade={grade.grade}
    >
      <s.Icon className="h-4 w-4" aria-hidden />
      Profile: {grade.label}
    </span>
  );
}

export function ReadinessGradeCard({ grade }: { grade: GradeView }) {
  const s = GRADE_STYLE[grade.grade];
  const current = GRADE_ORDER.indexOf(grade.grade);
  return (
    <section className="rounded-2xl bg-[var(--surface-card)] p-6 shadow-sm sm:p-8" aria-labelledby="readiness-grade-title" data-testid="readiness-grade-card">
      <div className="flex flex-col gap-6 md:flex-row md:items-center">
        <div
          className="flex h-28 w-28 shrink-0 flex-col items-center justify-center self-center rounded-full border-4"
          style={{ background: s.bg, borderColor: s.border, color: s.fg }}
        >
          <s.Icon className="h-9 w-9" aria-hidden />
          <span className="mt-1 text-base font-bold" data-testid="readiness-grade" data-grade={grade.grade}>
            {grade.label}
          </span>
        </div>
        <div className="min-w-0 flex-1">
          <h2 id="readiness-grade-title" className="text-xl font-bold text-[var(--text-primary)]">
            Your profile is {grade.label}
          </h2>
          <p className="mt-1 text-[var(--text-secondary)]">{grade.meaning}</p>
          {grade.howToMoveUp && (
            <p className="mt-2 font-medium text-[var(--text-primary)]" data-testid="readiness-grade-move-up">
              {grade.howToMoveUp}
            </p>
          )}
          <ol className="mt-4 flex flex-wrap items-center gap-2" aria-label="Grades">
            {GRADE_ORDER.map((g, i) => {
              const reached = i <= current;
              const st = GRADE_STYLE[g];
              return (
                <li
                  key={g}
                  className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm"
                  style={
                    reached
                      ? { background: st.bg, color: st.fg, borderColor: st.border, fontWeight: 600 }
                      : { background: 'var(--surface-muted)', color: 'var(--text-secondary)', borderColor: 'var(--border-default)' }
                  }
                  aria-current={i === current ? 'step' : undefined}
                  data-testid={`readiness-grade-step-${g}`}
                >
                  {reached ? <CheckCircle2 className="h-4 w-4" aria-hidden /> : <span aria-hidden className="h-4 w-4 rounded-full border border-current opacity-60" />}
                  {g === 'building' ? 'Building' : g === 'competitive' ? 'Competitive' : 'Strong'}
                  {i === current ? <span className="text-xs font-normal">(you are here)</span> : null}
                </li>
              );
            })}
          </ol>
          <p className="mt-3 text-sm text-[var(--text-muted)]" data-testid="readiness-parts-count">
            {grade.completeCount} of {grade.totalParts} parts complete
          </p>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ Parts */

export function ReadinessPartList({ parts }: { parts: ReadinessPartView[] }) {
  return (
    <section className="rounded-2xl bg-[var(--surface-card)] p-6 shadow-sm sm:p-8" aria-labelledby="readiness-parts-title">
      <h2 id="readiness-parts-title" className="mb-1 text-lg font-semibold text-[var(--text-primary)]">
        Your profile, part by part
      </h2>
      <p className="mb-5 text-sm text-[var(--text-secondary)]">Each part is Complete or Needs work, with the one thing to do next.</p>
      <ul className="space-y-3">
        {parts.map((p) => {
          const done = p.status === 'complete';
          return (
            <li
              key={p.key}
              className="rounded-xl border p-4"
              style={{
                borderColor: done ? 'var(--rr-success)' : 'var(--rr-warning)',
                borderLeftWidth: 6,
                background: 'var(--surface-card)',
              }}
              data-testid={`readiness-part-${p.key}`}
              data-status={p.status}
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h3 className="font-semibold text-[var(--text-primary)]">{p.label}</h3>
                <span
                  className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-sm font-semibold"
                  style={
                    done
                      ? { background: 'var(--rr-success-light)', color: 'var(--rr-success-fg, var(--rr-success))' }
                      : { background: 'var(--rr-warning-light)', color: 'var(--rr-warning-fg)' }
                  }
                  data-testid="readiness-part-status"
                >
                  {done ? <CheckCircle2 className="h-4 w-4" aria-hidden /> : <AlertCircle className="h-4 w-4" aria-hidden />}
                  {done ? 'Complete' : 'Needs work'}
                </span>
              </div>
              <p className="mt-1 text-sm text-[var(--text-secondary)]">{p.summary}</p>
              {!done && p.issues.length > 1 && (
                <ul className="mt-2 list-disc space-y-0.5 pl-5 text-sm text-[var(--text-secondary)]">
                  {p.issues.slice(1).map((i) => (
                    <li key={i}>{i}</li>
                  ))}
                </ul>
              )}
              {!done && p.action && (
                <div className="mt-3 flex flex-wrap items-center gap-3">
                  <Link to={p.action.href} className={SECONDARY_LINK} data-testid="readiness-part-action">
                    {p.action.label}
                  </Link>
                  {p.action.trainingCert && (
                    <Link
                      to={findTrainingHref(p.action.trainingCert)}
                      className="inline-flex items-center gap-1 text-sm font-medium text-[var(--rr-info)] underline"
                      data-testid="readiness-part-training"
                    >
                      <GraduationCap className="h-4 w-4" aria-hidden />
                      Find training near you
                    </Link>
                  )}
                  <span className="basis-full text-xs text-[var(--text-muted)]">{p.action.description}</span>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/* ------------------------------------------------------------------ Suggestions */

export function ReadinessSuggestionList({
  suggestions,
  onDismiss,
  heading = 'Optional ways to stand out',
  intro = 'Nice to have, never required. Hide any that do not fit you.',
}: {
  suggestions: ReadinessSuggestion[];
  onDismiss: (id: string) => void;
  heading?: string;
  intro?: string;
}) {
  return (
    <section className="rounded-2xl bg-[var(--surface-card)] p-6 shadow-sm sm:p-8" aria-labelledby="readiness-suggestions-title" data-testid="readiness-suggestions">
      <h2 id="readiness-suggestions-title" className="mb-1 flex items-center gap-2 text-lg font-semibold text-[var(--text-primary)]">
        <Sparkles className="h-5 w-5 text-[var(--rr-info)]" aria-hidden />
        {heading}
      </h2>
      <p className="mb-4 text-sm text-[var(--text-secondary)]" data-testid="readiness-suggestions-intro">{intro}</p>
      {suggestions.length === 0 ? (
        <p className="text-sm text-[var(--text-muted)]" data-testid="readiness-suggestions-empty">
          No more suggestions right now.
        </p>
      ) : (
        <ul className="space-y-3">
          {suggestions.map((s) => (
            <li
              key={s.id}
              className="flex flex-col gap-3 rounded-xl border border-[var(--border-subtle)] p-4 sm:flex-row sm:items-center"
              data-testid={`readiness-suggestion-${s.id}`}
            >
              <div className="min-w-0 flex-1">
                <p className="font-medium text-[var(--text-primary)]">{s.label}</p>
                <p className="text-sm text-[var(--text-secondary)]">{s.description}</p>
              </div>
              <div className="flex shrink-0 flex-wrap items-center gap-2">
                {s.trainingCert ? (
                  <Link to={findTrainingHref(s.trainingCert)} className={SECONDARY_LINK}>
                    Find training
                  </Link>
                ) : (
                  <Link to={s.href} className={SECONDARY_LINK}>
                    Add it
                  </Link>
                )}
                <button
                  type="button"
                  onClick={() => onDismiss(s.id)}
                  className="inline-flex min-h-10 items-center gap-1 rounded-lg px-3 text-sm font-medium text-[var(--text-secondary)] hover:bg-[var(--surface-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--rr-info)]"
                  aria-label={`Not now: ${s.label}`}
                  data-testid="readiness-suggestion-dismiss"
                >
                  <X className="h-4 w-4" aria-hidden />
                  Not now
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/* ------------------------------------------------------------------ Helper panel */

export function ReadinessHelperPanel({
  step,
  grade,
  variant = 'page',
}: {
  step: NextBestStep | null;
  grade: GradeView;
  variant?: 'page' | 'dashboard';
}) {
  const headingId = useId();
  return (
    <section
      className={`rounded-2xl border p-5 shadow-sm sm:p-6 ${variant === 'dashboard' ? 'mb-6' : ''}`}
      style={{ background: 'var(--rr-info-light)', borderColor: 'var(--rr-info)' }}
      aria-labelledby={headingId}
      data-testid="readiness-helper-panel"
      data-step={step?.id ?? 'none'}
    >
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
        <span
          className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[var(--surface-card)]"
          style={{ color: 'var(--rr-info)' }}
          aria-hidden
        >
          {step ? <Compass className="h-6 w-6" /> : <Trophy className="h-6 w-6" />}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p id={headingId} className="text-xs font-bold uppercase tracking-wider text-[var(--rr-info)]">
              {step ? 'Your next best step' : 'You are all set'}
            </p>
            {variant === 'dashboard' && <ReadinessGradeChip grade={grade} />}
          </div>
          <p className="mt-1 text-lg font-bold text-[var(--text-primary)]" data-testid="readiness-helper-title">
            {step ? step.label : 'Every part of your profile is complete.'}
          </p>
          <p className="text-sm text-[var(--text-secondary)]">
            {step ? `${step.description} ${step.reason}` : 'Keep your credentials current and keep an eye on new jobs.'}
          </p>
        </div>
        <Link
          to={step ? step.href : '/jobs'}
          className={`${PRIMARY_LINK} shrink-0`}
          style={{ background: 'var(--rr-red)' }}
          data-testid="readiness-helper-action"
        >
          {step ? (step.kind === 'renew' || step.kind === 'expiring' ? 'Renew it' : 'Do this now') : 'Browse jobs'}
        </Link>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ Celebration */

const celebratedKey = (uid: string) => `rr-readiness-celebrated:${uid}`;
function readLocal(uid: string): boolean {
  try {
    return window.localStorage.getItem(celebratedKey(uid)) === '1';
  } catch {
    return false;
  }
}
function writeLocal(uid: string, on: boolean) {
  try {
    if (on) window.localStorage.setItem(celebratedKey(uid), '1');
    else window.localStorage.removeItem(celebratedKey(uid));
  } catch {
    /* storage blocked: the profile flag still remembers */
  }
}

/**
 * Confetti once per completion: remembered in this browser AND in the profile
 * (preferences.readinessCelebrated), reset when the profile is no longer complete. With
 * prefers-reduced-motion there is no animation, only the static message.
 */
export function ReadinessCelebration({
  uid,
  profile,
  complete,
  title = 'Your profile is complete',
  body = 'Every part is marked Complete. Nice work. Keep your credentials current to stay there.',
}: {
  uid: string | undefined;
  profile: AnyProfile;
  complete: boolean;
  title?: string;
  body?: string;
}) {
  const reducedMotion = usePrefersReducedMotion();
  const profileFlag = prefsOf(profile).readinessCelebrated === true;
  const [show, setShow] = useState(false);
  const [animating, setAnimating] = useState(false);

  useEffect(() => {
    if (!uid) return;
    const action = celebrationAction(complete, readLocal(uid) || profileFlag);
    if (action === 'celebrate') {
      writeLocal(uid, true);
      setShow(true);
      setAnimating(true);
      upsertProfile(uid, { preferences: { readinessCelebrated: true } } as never).catch((err) =>
        console.warn('[readiness] could not save celebration flag', err),
      );
    } else if (action === 'reset') {
      writeLocal(uid, false);
      setShow(false);
      if (profileFlag) {
        upsertProfile(uid, { preferences: { readinessCelebrated: false } } as never).catch((err) =>
          console.warn('[readiness] could not reset celebration flag', err),
        );
      }
    }
  }, [uid, complete, profileFlag]);

  if (!show) return null;
  return (
    <>
      {animating && !reducedMotion && <Confetti onDone={() => setAnimating(false)} />}
      <div
        role="status"
        className="mb-6 flex items-start gap-3 rounded-2xl border p-4"
        style={{ background: 'var(--rr-success-light)', borderColor: 'var(--rr-success)' }}
        data-testid="readiness-celebration"
        data-motion={reducedMotion ? 'static' : 'confetti'}
      >
        <PartyPopper className="mt-0.5 h-6 w-6 shrink-0" style={{ color: 'var(--rr-success-fg, var(--rr-success))' }} aria-hidden />
        <div className="min-w-0 flex-1">
          <p className="font-bold" style={{ color: 'var(--rr-success-fg, var(--rr-success))' }}>
            {title}
          </p>
          <p className="text-sm text-[var(--text-secondary)]">{body}</p>
        </div>
        <button
          type="button"
          onClick={() => setShow(false)}
          className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-[var(--text-secondary)] hover:bg-[var(--surface-hover)]"
          aria-label="Close message"
          data-testid="readiness-celebration-close"
        >
          <X className="h-4 w-4" aria-hidden />
        </button>
      </div>
    </>
  );
}

/** The dashboard's compact block: celebration (when due) and the helper panel with the grade. */
export function DashboardReadinessHelper({ uid, profile }: { uid: string | undefined; profile: AnyProfile }) {
  const { dismissed } = useReadinessDismissals(uid, profile);
  const view = useEmtReadinessView(profile, dismissed);
  if (!view) return null;
  return (
    <>
      <ReadinessCelebration uid={uid} profile={profile} complete={view.complete} />
      <ReadinessHelperPanel step={view.step} grade={view.grade} variant="dashboard" />
    </>
  );
}
